/**
 * The per-cycle STEP a rule may be authored with on one column: how big one press of the stepper is,
 * how far the step may travel either way, and what it starts at.
 *
 * A rule steps in the column's OWN unit, so a reps rule steps in repetitions and a duration rule in
 * seconds. The LOAD is the one column that also gets to choose how its jump is MEASURED — a flat
 * number of units, or a percentage that compounds off what the client already lifts — and
 * `deltaUnitOptions` is the one place that answers which columns may offer that choice.
 *
 * That question is not the kg / %1RM segment above it in the sheet. The segment decides what the
 * load is WRITTEN IN (`prescriptionMode`); this decides how it GROWS. A %1RM-priced exercise with an
 * `Absolute` delta steps in percentage points of the max, and the same exercise with a `Percent`
 * delta compounds that percent.
 *
 * Three rules, because three different decisions:
 *   · The LOAD under a `Percent` delta is bounded by the ENGINE rather than by any column grid
 *     (`ProgressionDeltaBounds.MinLoadPercent`/`MaxLoadPercent`): −100 to +100, in whole percent,
 *     opening at +5. A percent has no plate grid, so the dictionary's increment says nothing about it.
 *   · The EFFORT columns carry authored ranges: ±3 points, seeded −1 for RIR and +0,5 for RPE. Those
 *     are programming judgements about how fast effort should be ramped, not arithmetic.
 *   · Every other column takes its increment from the field dictionary — the same grid a COMPUTED
 *     value on that column snaps to, so a step the trainer can author always lands on a value the
 *     projection can produce — and ranges ten of those either way. Ten bounds the CONTROL rather than
 *     the domain: the projection clamps into the field's own progression range anyway, so this only
 *     decides how far one stepper travels before typing in the matrix serves the trainer better.
 *
 * ── Ported from `move-with-me-frontend/.../plans/builder/model/progressionColumnStep.ts` ──────
 * 1:1, with the effort ranges inlined. They lived in mobile's `progressionPresets`, which this app's
 * port retired along with the three-family vocabulary that filled the rest of it; carrying a file
 * over for two constants would be keeping the shell of a module nothing else needs.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`.
 */

import { isEffortField, measurementFieldDefinition, type MeasurementFieldKey } from '~/shared/measurement';

import type { ProgressionDeltaUnit } from './progressionRules';

/** How far one press moves the per-cycle step, how far it may travel, and where a new rule starts. */
export interface ColumnStepParams {
  readonly step: number;
  readonly min: number;
  readonly max: number;
  /** The step a rule is seeded with when the trainer turns one on and has authored nothing. */
  readonly initial: number;
}

/** How many dictionary increments the stepper travels either way — see the module note. */
const STEP_RANGE_MULTIPLE = 10;

/** The load column, the one column whose seed is a programming convention rather than its own grid. */
const LOAD_FIELD: MeasurementFieldKey = 'weight';

/**
 * The jump a load rule OPENS on: 2,5 kg (2,5 percentage points on a %1RM-priced exercise).
 *
 * The dictionary's 1,25 is the SNAP GRID — the smallest plate pair a barbell can take, and therefore
 * what a computed kilogram rounds to. It is not what a linear-progression rule is written as: every
 * load screen in the handoff opens on "+2,5 kg" and flanks it with "+5" and "+1,25", which is the
 * pair `stepChipValues` produces from 2,5 and cannot produce from 1,25 (its half, 0,625, is off the
 * grid and is dropped, leaving one chip). Seeding from the grid opened the editor one plate below the
 * step every trainer then typed.
 *
 * Every other column keeps its dictionary increment: one repetition, one second, 0,1 km/h are the
 * units those columns are programmed in, and there is no second convention above them.
 */
const LOAD_SEED_STEP = 2.5;

/**
 * A percent jump's own control: whole percent, the engine's full reach either way, opening at +5.
 *
 * The numbers are `ProgressionDeltaBounds.MinLoadPercent`/`MaxLoadPercent` verbatim rather than a
 * narrower opinion of this app's — the backend refuses anything past them, and a control stopping
 * short would hide a rule the engine is happy to run. Whole percent because the projection compounds
 * `(1 + delta/100)` and a fractional percent is a prescription no requirement asks for.
 */
const PERCENT_STEP_PARAMS: ColumnStepParams = { step: 1, min: -100, max: 100, initial: 5 };

/**
 * The two effort scales' authored ranges. RIR seeds NEGATIVE — fewer reps in reserve is harder — and
 * RPE positive, which is the same direction stated in the two columns' own units.
 */
const EFFORT_STEP_PARAMS: Readonly<Record<'rpe' | 'rir', ColumnStepParams>> = {
  rpe: { step: 0.5, min: -3, max: 3, initial: 0.5 },
  rir: { step: 1, min: -3, max: 3, initial: -1 },
};

const ABSOLUTE_ONLY: readonly ProgressionDeltaUnit[] = ['Absolute'];
const LOAD_DELTA_UNITS: readonly ProgressionDeltaUnit[] = ['Absolute', 'Percent'];

/**
 * WHICH UNITS A JUMP ON `field` MAY BE MEASURED IN — the whole answer, in one place, because two
 * callers ask it for two different reasons and they may never disagree: the sheet asks whether there
 * is a choice to render (`length > 1`), and the write asks whether a chosen unit may be WRITTEN.
 *
 * Only the load may carry `Percent`. `ProgressionDeltaBounds.Validate` throws for a percent delta on
 * every other column — a fraction of a repetition or of a second is a prescription nothing can
 * author and nothing downstream can round — so a document carrying one 400s the whole plan save.
 */
export function deltaUnitOptions(field: MeasurementFieldKey): readonly ProgressionDeltaUnit[] {
  return field === LOAD_FIELD ? LOAD_DELTA_UNITS : ABSOLUTE_ONLY;
}

/**
 * The step params for authoring a rule on `field` in `unit`, or null for a column with no increment
 * at all — a text notation, which nothing can progress and no surface ever offers.
 *
 * The unit defaults to `Absolute` because that is what every column but the load can be, and because
 * a caller with no rule in hand yet is asking about the ordinary case.
 */
export function columnStepParams(
  field: MeasurementFieldKey,
  unit: ProgressionDeltaUnit = 'Absolute',
): ColumnStepParams | null {
  if (unit === 'Percent' && deltaUnitOptions(field).includes('Percent')) return PERCENT_STEP_PARAMS;
  if (isEffortField(field)) return EFFORT_STEP_PARAMS[field === 'rir' ? 'rir' : 'rpe'];
  const { progressionStep } = measurementFieldDefinition(field);
  if (progressionStep === null) return null;
  return {
    step: progressionStep,
    min: -STEP_RANGE_MULTIPLE * progressionStep,
    max: STEP_RANGE_MULTIPLE * progressionStep,
    // Movements progress upward by default; a trainer wanting a taper types the minus in.
    initial: field === LOAD_FIELD ? LOAD_SEED_STEP : progressionStep,
  };
}

/**
 * The two shortcuts offered beside the stepper: the same step DOUBLED and HALVED — the handoff
 * flanks "+2,5 kg" with "+5" and "+1,25".
 *
 * Twice and half rather than two fixed numbers, because the useful neighbours of a step are relative
 * to it: on a 2,5 kg jump the interesting alternatives are 5 and 1,25, and on a one-repetition jump
 * they are 2 and nothing. Each is kept only when it lands ON the column's own increment grid and
 * inside the stepper's range — a chip offering a step the projection would snap away is a control
 * that lies about what it does.
 */
export function stepChipValues(
  field: MeasurementFieldKey,
  current: number,
  unit: ProgressionDeltaUnit = 'Absolute',
): number[] {
  const params = columnStepParams(field, unit);
  if (params === null || current === 0) return [];
  const grid = params.step;
  return [current * 2, current / 2].filter((candidate) => {
    if (candidate < params.min || candidate > params.max || candidate === current) return false;
    // Land on the grid — with a tolerance, because 1,25 / 2 is representable and 0,1 / 2 is not.
    const steps = candidate / grid;
    return Math.abs(steps - Math.round(steps)) < 1e-9;
  });
}
